import React, { useEffect, useRef } from 'react';
import './MobileTopBar.css';
import rdvWideImg from '../../assets/rdv-logoW.png';

/**
 * Slim top bar for the mobile shell (AuthenticatedLayout ≤768px). Brand only —
 * the menu trigger lives in MobileBottomBar.
 *
 * Scroll-linked hide: the bar slides/fades out in proportion to the first
 * ~96px of scroll and returns as you scroll back to the top. Every screen
 * scrolls a DIFFERENT inner container (projects grid, project view, the
 * deliverables panel…), so a capture-phase document listener hears them all —
 * scroll events don't bubble, but they do capture through ancestors. The
 * progress lands in a CSS variable; the stylesheet owns the motion.
 */
const MobileTopBar = ({ title }) => {
  const barRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const HIDE_DISTANCE = 96; // px of scroll for a full hide

    const onScroll = (e) => {
      const t = e.target;
      // Elements only (document scroll doesn't happen in this shell), and
      // only VERTICAL scrollers — the breadcrumb pill scrolls sideways and
      // its scrollTop of 0 would wrongly snap the bar back mid-gesture.
      if (!t || !t.scrollHeight || !(t.scrollHeight - t.clientHeight > 24)) {
        return;
      }
      const progress = Math.min(1, Math.max(0, t.scrollTop / HIDE_DISTANCE));
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        // Published on the ROOT: the bar's slide AND the shell's reserved
        // top offsets both consume it, so content rises into the freed
        // strip in lock-step with the bar leaving.
        document.documentElement.style.setProperty('--tb-hide', String(progress));
      });
    };

    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('scroll', onScroll, true);
      cancelAnimationFrame(rafRef.current);
      // Leaving the mobile shell (rotation/desktop) must not strand a
      // collapsed offset on other layouts.
      document.documentElement.style.removeProperty('--tb-hide');
    };
  }, []);

  return (
    <header className='mobile-top-bar' ref={barRef}>
      <div className='mobile-top-bar__brand'>
        <img src={rdvWideImg} alt='RDV' className='mobile-top-bar__logo' />
        {title ? <span className='mobile-top-bar__title'>{title}</span> : null}
      </div>
    </header>
  );
};

export default MobileTopBar;
